import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Printer, Download, X, Phone, Mail, MapPin, Calendar, CreditCard, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import { useSettings } from '../context/SettingsContext';
import ThermalReceipt from './ThermalReceipt';
import PharmacyLogo from './PharmacyLogo';
import { formatPakistanDate, formatPakistanDateTime } from '../utils/pakistanTime';

function StandardSaleLayout({ sale, settings, pharmacyName, pharmacyAddress, pharmacyPhone, pharmacyEmail, saleDate, items, subtotal, discount, tax, grandTotal, paidAmount, balanceDue, paymentStatus, customerName, customerPhone, customerAddress }) {
  return (
    <div className="w-full max-w-3xl bg-white p-8 border border-gray-200 rounded-xl shadow-sm print:border-none print:shadow-none print:p-0 text-gray-800">
      {/* Header: Pharmacy & Invoice info */}
      <div className="flex justify-between items-start border-b-2 border-emerald-600 pb-6 mb-6">
        <div className="flex items-start gap-3">
          <PharmacyLogo logo={settings?.pharmacy_logo} alt={`${pharmacyName} logo`} className="h-12 w-12 shrink-0 rounded-lg" imageClassName="h-12 w-12 shrink-0 rounded-lg object-contain" />
          <div>
          <h1 className="text-2xl font-black text-emerald-800 tracking-tight">{pharmacyName}</h1>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 text-gray-400" /> {pharmacyAddress}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
            {pharmacyPhone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-gray-400" /> {pharmacyPhone}</span>}
            {pharmacyEmail && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-gray-400" /> {pharmacyEmail}</span>}
          </div>
          </div>
        </div>

        <div className="text-right">
          <span className="inline-block bg-emerald-50 text-emerald-800 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-md border border-emerald-200">
            Sale Invoice
          </span>
          <p className="font-mono text-base font-bold text-gray-900 mt-2">{sale.invoice_number}</p>
          <p className="text-xs text-gray-500 mt-0.5">Date: {saleDate}</p>
          <p className="text-xs text-gray-500">Method: <span className="font-semibold uppercase text-gray-800">{sale.payment_method || 'cash'}</span></p>
        </div>
      </div>

      {/* Customer & Status Details Banner */}
      <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50/80 p-4 rounded-xl border border-gray-100 text-xs">
        <div>
          <p className="font-bold text-[11px] text-gray-500 uppercase tracking-wider mb-1">Customer Details</p>
          <p className="text-sm font-bold text-gray-900">{customerName || 'Walk-in Customer'}</p>
          {customerPhone && <p className="text-gray-600">Phone: {customerPhone}</p>}
          {customerAddress && <p className="text-gray-600">Address: {customerAddress}</p>}
        </div>

        <div className="text-right flex flex-col justify-between items-end">
          <div>
            <p className="font-bold text-[11px] text-gray-500 uppercase tracking-wider mb-1">Payment Status</p>
            <span
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider',
                paymentStatus === 'paid'
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                  : paymentStatus === 'partial'
                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                  : 'bg-red-100 text-red-800 border border-red-200'
              )}
            >
              {paymentStatus === 'paid' && <CheckCircle2 className="h-3.5 w-3.5" />}
              {paymentStatus === 'partial' && <Clock className="h-3.5 w-3.5" />}
              {paymentStatus === 'unpaid' && <AlertCircle className="h-3.5 w-3.5" />}
              {paymentStatus === 'unpaid' ? 'Pending (Unpaid)' : paymentStatus}
            </span>
          </div>
          {(sale.cashier_name || sale.user_name || sale.cashier) && (
            <p className="text-[11px] text-gray-500 mt-2">
              Billed by: <span className="font-semibold text-gray-800">{sale.cashier_name || sale.user_name || sale.cashier}</span>
              {(sale.cashier_role || sale.role) && (
                <span className="ml-1.5 inline-block rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                  {sale.cashier_role || sale.role}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-6 overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-bold uppercase text-[10px]">
              <th className="py-2.5 px-3">#</th>
              <th className="py-2.5 px-3">Medicine / Description</th>
              <th className="py-2.5 px-3">Batch & Expiry</th>
              <th className="py-2.5 px-3 text-center">Unit</th>
              <th className="py-2.5 px-3 text-center">Qty</th>
              <th className="py-2.5 px-3 text-right">Price</th>
              <th className="py-2.5 px-3 text-right">Discount</th>
              <th className="py-2.5 px-3 text-right">Total (Rs.)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-gray-700">
            {items.map((item, idx) => {
              const uQty = item.quantity || item.unit_quantity || 1;
              const uName = item.unit_name || item.base_unit || 'units';
              const price = parseFloat(item.unit_price) || 0;
              const itemDisc = parseFloat(item.discount) || 0;
              const rowTotal = parseFloat(item.total) || (uQty * price - itemDisc);

              return (
                <tr key={idx} className="hover:bg-gray-50/50">
                  <td className="py-2.5 px-3 text-gray-400 font-mono">{idx + 1}</td>
                  <td className="py-2.5 px-3 font-bold text-gray-900">
                    {item.medicine_name || `Medicine #${item.medicine_id}`}
                  </td>
                  <td className="py-2.5 px-3 text-[11px]">
                    {item.batch_number && <span className="font-mono font-semibold text-gray-800">{item.batch_number}</span>}
                    {item.expiry_date && (
                      <span className="block text-[10px] text-gray-500">
                        Exp: {new Date(item.expiry_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center font-semibold text-gray-800">{uName}</td>
                  <td className="py-2.5 px-3 text-center font-bold text-gray-900">{uQty}</td>
                  <td className="py-2.5 px-3 text-right font-medium">Rs. {price.toFixed(2)}</td>
                  <td className="py-2.5 px-3 text-right text-gray-500">{itemDisc > 0 ? `Rs. ${itemDisc.toFixed(2)}` : '-'}</td>
                  <td className="py-2.5 px-3 text-right font-bold text-gray-900">Rs. {rowTotal.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals & Notes Section */}
      <div className="grid grid-cols-2 gap-6 items-start mb-8">
        <div>
          {sale.notes && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs">
              <p className="font-bold text-gray-600 uppercase text-[10px] tracking-wider mb-1">Notes / Remarks:</p>
              <p className="text-gray-700 whitespace-pre-wrap">{sale.notes}</p>
            </div>
          )}
          {sale.customer_balance !== undefined && parseFloat(sale.customer_balance) > 0 && (
            <div className="mt-3 p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-900 flex justify-between">
              <span className="font-medium">Customer Current Balance Due:</span>
              <span className="font-bold">Rs. {parseFloat(sale.customer_balance).toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="space-y-2 text-xs bg-gray-50/80 p-4 rounded-xl border border-gray-200">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal:</span>
            <span className="font-semibold text-gray-900">Rs. {subtotal.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Discount:</span>
              <span className="font-semibold text-amber-600">- Rs. {discount.toFixed(2)}</span>
            </div>
          )}
          {tax > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Tax:</span>
              <span className="font-semibold text-gray-900">+ Rs. {tax.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-black border-t-2 border-gray-300 pt-2 text-gray-900">
            <span>Grand Total:</span>
            <span className="text-emerald-700">Rs. {grandTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-700 font-semibold pt-1 border-t border-gray-200">
            <span>Amount Paid:</span>
            <span className="text-gray-900">Rs. {paidAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold pt-1">
            <span className={balanceDue > 0 ? 'text-red-600' : 'text-gray-600'}>Balance Due:</span>
            <span className={clsx('text-sm', balanceDue > 0 ? 'text-red-600' : 'text-emerald-600')}>
              Rs. {balanceDue.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-2 gap-8 pt-10 border-t border-gray-200 text-center text-xs text-gray-500">
        <div>
          <div className="border-b border-gray-400 mb-1 w-1/2 mx-auto" />
          <p className="font-medium">Prepared By (Pharmacist)</p>
        </div>
        <div>
          <div className="border-b border-gray-400 mb-1 w-1/2 mx-auto" />
          <p className="font-medium">Customer Signature</p>
        </div>
      </div>

      {/* System Footer */}
      <div className="mt-8 text-center text-[9px] text-gray-400 border-t border-gray-100 pt-3">
        <p>Gaza Pharmacy Management System • Generated on {formatPakistanDateTime(new Date())}</p>
      </div>
    </div>
  );
}

export default function SaleInvoice({ sale, customer, onClose }) {
  const { settings } = useSettings();
  const [printLayout, setPrintLayout] = useState('standard'); // 'standard' (A4) or 'thermal' (80mm)

  if (!sale) return null;

  const pharmacyName = settings?.pharmacy_name || 'Gaza Pharmacy';
  const pharmacyAddress = settings?.pharmacy_address || 'Gaza City, Main Street';
  const pharmacyPhone = settings?.pharmacy_phone || '0599123456';
  const pharmacyEmail = settings?.pharmacy_email || 'info@mypharmacy.com';

  const saleDate = sale.sale_date || sale.created_at
    ? formatPakistanDate(sale.sale_date || sale.created_at, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

  const items = sale.items || [];
  const subtotal = parseFloat(sale.subtotal) || 0;
  const discount = parseFloat(sale.discount) || 0;
  const tax = parseFloat(sale.tax) || 0;
  const grandTotal = parseFloat(sale.total) || subtotal + tax - discount;
  const paidAmount = parseFloat(sale.paid_amount) || 0;
  const balanceDue = parseFloat(sale.balance_due) || Math.max(0, grandTotal - paidAmount);
  const paymentStatus = sale.payment_status || (balanceDue > 0 ? (paidAmount > 0 ? 'partial' : 'unpaid') : 'paid');
  const customerName = customer?.name || sale.customer_name || 'Walk-in Customer';
  const customerPhone = customer?.phone || sale.customer_phone || '';
  const customerAddress = customer?.address || sale.customer_address || '';

  const handlePrint = () => {
    window.print();
  };

  const layoutProps = {
    sale,
    settings,
    pharmacyName,
    pharmacyAddress,
    pharmacyPhone,
    pharmacyEmail,
    saleDate,
    items,
    subtotal,
    discount,
    tax,
    grandTotal,
    paidAmount,
    balanceDue,
    paymentStatus,
    customerName,
    customerPhone,
    customerAddress,
  };

  const thermalData = {
    invoice: sale.invoice_number || `INV-${sale.id}`,
    date: sale.created_at || sale.sale_date || new Date(),
    items: items.map((it) => ({
      name: it.medicine_name || it.name || `Medicine #${it.medicine_id}`,
      medicine_name: it.medicine_name || it.name || `Medicine #${it.medicine_id}`,
      quantity: it.quantity || it.unit_quantity || 1,
      unit_quantity: it.unit_quantity || it.quantity || 1,
      unit_price: it.unit_price || it.price || 0,
      total: it.total || ((it.quantity || it.unit_quantity || 1) * (it.unit_price || it.price || 0)),
      selected_unit: it.unit_name || it.selected_unit || it.base_unit || '',
      unit_name: it.unit_name || it.selected_unit || it.base_unit || '',
      batch_number: it.batch_number,
    })),
    subtotal,
    discount,
    discountType: sale.discount_type || 'fixed',
    taxAmount: tax,
    grandTotal,
    paidAmount,
    paymentMethod: sale.payment_method || 'cash',
    change: parseFloat(sale.change_amount) || 0,
    customer: {
      name: customerName,
      phone: customerPhone,
    },
    customerBalance: parseFloat(customer?.balance || sale.customer_balance) || 0,
    projectedBalance: parseFloat(customer?.balance || sale.customer_balance) || 0,
    cashier: sale.cashier_name || sale.user_name || sale.cashier || 'Admin',
    cashier_role: sale.cashier_role || sale.role || 'Admin',
    settings,
  };

  return (
    <>
      {/* On-Screen Modal View */}
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 overflow-y-auto print:hidden">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs"
          onClick={onClose}
        />

        {/* Main Container */}
        <div className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-10">
          
          {/* Modal Controls Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/90 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                <Printer className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Sale Invoice</h3>
                <p className="text-xs font-mono text-gray-500">{sale.invoice_number}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex bg-gray-200/80 p-0.5 rounded-lg text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setPrintLayout('standard')}
                  className={clsx(
                    'px-3 py-1.5 rounded-md transition-colors',
                    printLayout === 'standard' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  Standard (A4)
                </button>
                <button
                  type="button"
                  onClick={() => setPrintLayout('thermal')}
                  className={clsx(
                    'px-3 py-1.5 rounded-md transition-colors',
                    printLayout === 'thermal' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  Thermal (80mm)
                </button>
              </div>

              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 shadow-sm active:scale-95 transition-all"
              >
                <Printer className="h-4 w-4" /> Print / Save PDF
              </button>

              <button
                onClick={onClose}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-200/70 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Preview Content Body */}
          <div className="flex-1 p-6 overflow-y-auto bg-gray-100 flex justify-center">
            {printLayout === 'standard' ? (
              <StandardSaleLayout {...layoutProps} />
            ) : (
              <div className="w-[80mm] max-w-full bg-white shadow-md border border-gray-200 rounded-lg p-2">
                <ThermalReceipt data={thermalData} />
              </div>
            )}
          </div>

        </div>
      </div>

      {/* DEDICATED PRINT CONTAINER MOUNTED AT BODY ROOT (OUTSIDE #root & .fixed) */}
      {createPortal(
        printLayout === 'standard' ? (
          <div id="printable-sales-export" className="printable-document hidden print:block">
            <StandardSaleLayout {...layoutProps} />
          </div>
        ) : (
          <div id="printable-thermal-receipt" className="hidden print:block">
            <ThermalReceipt data={thermalData} />
          </div>
        ),
        document.body
      )}
    </>
  );
}
